import type { FC, ReactNode } from 'react'
import type { TripFormValues } from '@/components/trip/trip-form'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowLeft, Link2, Scale, Users } from 'lucide-react'
import { createTripRequest } from '@/lib/trip-client'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { TripForm, budgetToCents } from '@/components/trip/trip-form'

const INITIAL: TripFormValues = {
    name: '',
    destination: '',
    startDate: '',
    endDate: '',
    currency: 'USD',
    budget: '',
    cover: 'city',
    travelers: ['', '']
}

const TIPS = [
    { icon: Users, text: 'Add everyone now. You can add late arrivals later.' },
    { icon: Link2, text: 'You get a private link to share with the group.' },
    { icon: Scale, text: 'Balances update the moment anyone adds a cost.' }
]

const NewTrip: FC = (): ReactNode => {
    const router = useRouter()

    const handleSubmit = async (values: TripFormValues) => {
        const shareCode = await createTripRequest({
            name: values.name,
            destination: values.destination,
            startDate: values.startDate || null,
            endDate: values.endDate || null,
            currency: values.currency,
            budgetCents: budgetToCents(values.budget, values.currency),
            cover: values.cover,
            travelers: values.travelers
        })

        await router.push(`/trips/${shareCode}?welcome=1`)
    }

    return (
        <Layout>
            <Seo
                title='Start a trip'
                description='Create a shared trip in seconds. Add your travelers, pick a currency and get a private link for the whole group.'
            />

            <section id='start-trip' className='relative overflow-hidden'>
                <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-72 bg-linear-to-b from-secondary/70 to-transparent' />

                <div className='relative mx-auto grid max-w-6xl gap-10 px-4 pt-10 pb-20 sm:px-6 lg:grid-cols-[1fr_1.15fr] lg:gap-16 lg:pt-16'>
                    <div className='flex flex-col gap-6'>
                        <Link href='/' className='inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground'>
                            <ArrowLeft className='size-4' />
                            Back to home
                        </Link>

                        <div className='flex flex-col gap-4'>
                            <p className='text-sm font-bold tracking-wide text-primary uppercase'>
                                New trip
                            </p>

                            <h1 className='font-heading text-4xl leading-[1.05] font-bold tracking-tight text-balance sm:text-5xl'>
                                Set up the shared tab
                            </h1>

                            <p className='max-w-md text-lg leading-relaxed text-muted-foreground'>
                                Name the trip, add the crew and pick the currency you will spend in. No accounts, no sign-up.
                            </p>
                        </div>

                        <ul className='flex flex-col gap-3'>
                            {TIPS.map(tip => (
                                <li key={tip.text} className='flex items-start gap-3'>
                                    <span className='mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-card text-primary shadow-sm ring-1 ring-border'>
                                        <tip.icon className='size-4' />
                                    </span>

                                    <span className='pt-1 text-sm leading-relaxed text-foreground/80'>
                                        {tip.text}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='rounded-3xl bg-card p-5 shadow-xl shadow-foreground/5 ring-1 ring-border sm:p-8'>
                        <TripForm
                            initial={INITIAL}
                            withTravelers
                            submitLabel='Create trip'
                            onSubmit={handleSubmit}
                        />
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default NewTrip
